import { router } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';

import { AttachmentsStaging, type StagedAttachment } from './attachments-staging';
import { WorkOrderForm } from './form';

import { useSession } from '@/components/auth';
import { ErrorBanner } from '@/components/common/error-banner';
import { useNotification } from '@/components/notification-system';
import { useTheme } from '@/components/theme-provider';
import { Button, Text, TextInput } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { usePermissions } from '@/hooks/use-permissions';
import { useSubmit } from '@/hooks/use-submit';
import { dataService, DuplicateWorkOrderError } from '@/services/data-service';
import { WorkOrderStatus, type Association, type WorkOrder, type WorkOrderFormData } from '@/types/models';
import { ModelPartial } from '@/utils/partial-type';
import { claimTouchResponder } from '@/utils/responder';
import { resolveStagedFilesAfterSubmit, uploadStagedAttachments } from '@/utils/staged-upload';
import { getNonUpdatableWorkOrderFields, NON_ADMIN_LOCKED_WORK_ORDER_FIELDS } from '@/utils/work-order-field-policy';

interface DuplicateInfo {
	existingWorkOrderId: string;
	existingOrderNumber: number;
	formData: any;
}

interface CreateWorkOrderProps {
	association?: Association | null;
	status?: string;
	backURL?: any;
	showRecurrenceFields?: boolean;
	initialValues?: ModelPartial<WorkOrderFormData>;
	/** Seed the lot (address + owner) from this account — `/work-orders/new?accountId=`. */
	initialAccountId?: string;
}

// Module-level so the default is the SAME object every render: an inline `= {}` is a new one
// each time, which rebuilds `initialVals` and makes WorkOrderForm's reset effect wipe whatever
// the user has typed on any rerender (e.g. `/me/feature-flags` resolving on /quotes/new).
const NO_INITIAL_VALUES: ModelPartial<WorkOrderFormData> = {};

export const CreateWorkOrder: React.FC<CreateWorkOrderProps> = ({
	association = null,
	status = 'open',
	backURL,
	showRecurrenceFields = false,
	initialValues: additionalInitialValues = NO_INITIAL_VALUES,
	initialAccountId,
}) => {
	const { isSubmitting, error, setError, run } = useSubmit();
	const { showModal, showToast } = useNotification();
	const { uploadFileToServer, user } = useApi();
	const { isWorkOrderAdmin } = usePermissions();
	const { session } = useSession();
	const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null);
	const [ackInput, setAckInput] = useState('');
	// Bumping this key remounts the form so "Save & add another" gets a clean
	// slate (associations are preserved via initialVals below). (#116)
	const [formKey, setFormKey] = useState(0);
	// What "Save & add another" carries into the remounted form: the association
	// and Date of Order the user just saved with. Everything else resets. (#570)
	const [carryOver, setCarryOver] = useState<Pick<WorkOrderFormData, 'association' | 'createdAt'> | null>(null);
	// On create the order number is settable and the creator is server-assigned, so of
	// the edit locks only the admin-only audit field (Date of Order) applies.
	// Same call as the edit screen (app/work-orders/[id]/edit.tsx).
	const readOnlyFields = useMemo(() => {
		const locked = getNonUpdatableWorkOrderFields(user, isWorkOrderAdmin);
		return new Set<string>(NON_ADMIN_LOCKED_WORK_ORDER_FIELDS.filter((field) => locked.has(field)));
	}, [user, isWorkOrderAdmin]);
	// Track whether the pending submission came from "Save & add another" so
	// the duplicate-ack flow can route the confirmation down the same path.
	const [addAnotherMode, setAddAnotherMode] = useState(false);
	// Files picked before the WO exists; uploaded sequentially after create. (#126)
	const [stagedFiles, setStagedFiles] = useState<StagedAttachment[]>([]);

	const buildPayload = (formData: any, ack?: { duplicateAckId: string; duplicateAckTimestamp: number }) => ({
		orderNumber: formData.orderNumber,
		workRequested: formData.workRequested,
		jobLocation: formData.jobLocation,
		terms: formData.terms,
		status: formData.status || WorkOrderStatus.Open,
		priority: formData.priority,
		dueDate: formData.dueDate,
		// Honoured for admins only; the server ignores it for everyone else (#570).
		createdAt: formData.createdAt,
		assignedTo: formData.assignedTo?.id,
		associationId: formData.association.id,
		createdBy: formData.createdBy,
		ownerName: formData.ownerName,
		ownerPhone: formData.ownerPhone,
		ownerEmail: formData.ownerEmail,
		estimatedCost: formData.estimatedCost,
		recurrencePeriod: formData.recurrencePeriod,
		recurrenceValue: formData.recurrenceValue,
		followUpNeeded: formData.followUpNeeded,
		followUpNote: formData.followUpNote,
		...ack,
	});

	const uploadStagedFilesFor = useCallback(async (
		workOrder: WorkOrder,
		filesToUpload: StagedAttachment[],
	) => {
		if (filesToUpload.length === 0) return { uploaded: [], failed: [] };
		return uploadStagedAttachments(filesToUpload, workOrder.id, {
			uploadFileToServer,
			createAttachment: (data) => dataService.createAttachment(data),
			isDemoMode: Boolean(session?.isDemoMode),
		});
	}, [uploadFileToServer, session?.isDemoMode]);

	// After each submit attempt, apply the pure decision from
	// resolveStagedFilesAfterSubmit — prune every attempted file and
	// emit a toast keyed to success or failure. Kept out of the caller
	// sites so the three handlers (create, add-another, duplicate-confirm)
	// can't drift in behavior.
	const applyUploadOutcome = useCallback((
		workOrder: WorkOrder,
		filesAtSubmit: StagedAttachment[],
		result: { uploaded: StagedAttachment[]; failed: StagedAttachment[] },
	) => {
		const label = `Work Order #${workOrder.orderNumber ?? workOrder.id}`;
		const decision = resolveStagedFilesAfterSubmit({
			currentStaged: stagedFiles,
			filesAtSubmit,
			result,
			workOrderLabel: label,
		});
		setStagedFiles(decision.nextStaged);
		if (decision.toast) {
			showToast(decision.toast.message, { variant: decision.toast.variant });
		}
	}, [stagedFiles, showToast]);

	const startNextEntry = (formData: any) => {
		setCarryOver({ association: formData.association, createdAt: formData.createdAt });
		setFormKey(k => k + 1);
	};

	const handleCreate = async (formData: any) => {
		setAddAnotherMode(false);
		try {
			if (!formData.association?.id) {
				showModal({ title: 'Error', message: 'Please select an association', variant: 'error' });
				return;
			}

			// Snapshot the files that existed at submit time. `run()` wraps
			// BOTH the create call and the upload loop so `isSubmitting` stays
			// true until uploads settle — prevents the staging UI from
			// re-enabling mid-upload. (PR #127 round-2 finding #3.)
			const filesAtSubmit = stagedFiles;
			const { workOrder, uploadResult } = await run(async () => {
				const wo = await dataService.createWorkOrder(buildPayload(formData));
				const up = await uploadStagedFilesFor(wo, filesAtSubmit);
				return { workOrder: wo, uploadResult: up };
			});
			applyUploadOutcome(workOrder, filesAtSubmit, uploadResult);
			router.replace(`/work-orders/${workOrder.id}`);
		} catch (e) {
			if (e instanceof DuplicateWorkOrderError) {
				setDuplicateInfo({
					existingWorkOrderId: e.existingWorkOrderId,
					existingOrderNumber: e.existingOrderNumber,
					formData,
				});
				setAckInput('');
				setError(null);
				return;
			}
			console.error('Error creating work order:', e);
			const message = e instanceof Error ? e.message : 'Failed to create work order';
			setError(new Error(message));
			showModal({ title: 'Error', message, variant: 'error' });
		}
	};

	const handleCreateAndAddAnother = async (formData: any) => {
		setAddAnotherMode(true);
		try {
			if (!formData.association?.id) {
				showModal({ title: 'Error', message: 'Please select an association', variant: 'error' });
				return;
			}

			const filesAtSubmit = stagedFiles;
			const { workOrder, uploadResult } = await run(async () => {
				const wo = await dataService.createWorkOrder(buildPayload(formData));
				const up = await uploadStagedFilesFor(wo, filesAtSubmit);
				return { workOrder: wo, uploadResult: up };
			});
			// Prune every attempted file — no failed photo can leak into the
			// next WO's submission (PR #127 round-3 #2).
			applyUploadOutcome(workOrder, filesAtSubmit, uploadResult);
			// Preserve the association and date for the next entry — everything else resets.
			showToast(`Work order #${workOrder.orderNumber ?? workOrder.id} created. Add another.`);
			startNextEntry(formData);
		} catch (e) {
			if (e instanceof DuplicateWorkOrderError) {
				setDuplicateInfo({
					existingWorkOrderId: e.existingWorkOrderId,
					existingOrderNumber: e.existingOrderNumber,
					formData,
				});
				setAckInput('');
				setError(null);
				return;
			}
			console.error('Error creating work order:', e);
			const message = e instanceof Error ? e.message : 'Failed to create work order';
			setError(new Error(message));
			showModal({ title: 'Error', message, variant: 'error' });
		}
	};

	const handleDuplicateConfirm = async () => {
		if (!duplicateInfo || ackInput.trim() !== duplicateInfo.existingWorkOrderId) return;

		try {
			const filesAtSubmit = stagedFiles;
			const { workOrder, uploadResult } = await run(async () => {
				const wo = await dataService.createWorkOrder(
					buildPayload(duplicateInfo.formData, {
						duplicateAckId: duplicateInfo.existingWorkOrderId,
						duplicateAckTimestamp: Date.now(),
					}),
				);
				const up = await uploadStagedFilesFor(wo, filesAtSubmit);
				return { workOrder: wo, uploadResult: up };
			});
			setDuplicateInfo(null);
			setAckInput('');
			applyUploadOutcome(workOrder, filesAtSubmit, uploadResult);
			if (addAnotherMode) {
				showToast(`Work order #${workOrder.orderNumber ?? workOrder.id} created. Add another.`);
				startNextEntry(duplicateInfo.formData);
				setAddAnotherMode(false);
				return;
			}
			router.replace(`/work-orders/${workOrder.id}`);
		} catch (e) {
			console.error('Error creating duplicate work order:', e);
			const message = e instanceof Error ? e.message : 'Failed to create work order';
			setError(new Error(message));
			showModal({ title: 'Error', message, variant: 'error' });
			setDuplicateInfo(null);
			setAckInput('');
		}
	};

	const initialVals = useMemo(() => ({
		...additionalInitialValues,
		association: carryOver?.association ?? association ?? additionalInitialValues.association,
		createdAt: carryOver?.createdAt ?? additionalInitialValues.createdAt,
		status: status as WorkOrderStatus,
	}), [association, status, additionalInitialValues, carryOver]);

	return (
		<>
			<ErrorBanner error={error} />
			<WorkOrderForm
				key={formKey}
				isCreate={true}
				showRecurrenceFields={showRecurrenceFields}
				onSave={handleCreate}
				onSaveAndAddAnother={handleCreateAndAddAnother}
				onCancel={() => router.replace('/')}
				initialValues={initialVals}
				isSubmitting={isSubmitting}
				readOnlyFields={readOnlyFields}
				// Only the first entry: "Save & add another" carries the association, not the lot.
				initialAccountId={formKey === 0 ? initialAccountId : undefined}
			>
				<AttachmentsStaging
					value={stagedFiles}
					onChange={setStagedFiles}
					disabled={isSubmitting}
				/>
			</WorkOrderForm>
			{duplicateInfo && (
				<DuplicateConfirmModal
					duplicateInfo={duplicateInfo}
					ackInput={ackInput}
					onAckInputChange={setAckInput}
					onConfirm={handleDuplicateConfirm}
					// TODO(PR#127-L1): reset addAnotherMode here too. If the user
					// cancels after clicking "Save & add another", the flag stays
					// true. Benign today (only read inside handleDuplicateConfirm,
					// which won't fire after Cancel), but a latent foot-gun — fold
					// both exit paths through one cleanup function.
					onCancel={() => { setDuplicateInfo(null); setAckInput(''); }}
					isSubmitting={isSubmitting}
				/>
			)}
		</>
	);
};

interface DuplicateConfirmModalProps {
	duplicateInfo: DuplicateInfo;
	ackInput: string;
	onAckInputChange: (value: string) => void;
	onConfirm: () => void;
	onCancel: () => void;
	isSubmitting: boolean;
}

const DuplicateConfirmModal: React.FC<DuplicateConfirmModalProps> = ({
	duplicateInfo,
	ackInput,
	onAckInputChange,
	onConfirm,
	onCancel,
	isSubmitting,
}) => {
	const theme = useTheme();
	const idMatches = ackInput.trim() === duplicateInfo.existingWorkOrderId;

	return (
		<Modal visible transparent animationType="fade" onRequestClose={onCancel}>
			<Pressable style={styles.backdrop} onPress={onCancel}>
				<View
					style={[styles.container, { backgroundColor: theme.colors.background, borderLeftColor: theme.colors.tertiary, borderLeftWidth: 4 }]}
					testID="duplicate-confirm-modal"
					{...claimTouchResponder}
				>
					<Text variant="headlineSmall" style={styles.title}>
						Duplicate Work Order Detected
					</Text>

					<Text variant="bodyMedium" style={styles.message}>
						A work order with identical details already exists:
					</Text>

					<View style={[styles.existingInfo, { backgroundColor: theme.colors.surfaceVariant }]}>
						<Text variant="labelLarge">
							Existing Work Order #{duplicateInfo.existingOrderNumber}
						</Text>
						<Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant, marginTop: 4 }}>
							ID: {duplicateInfo.existingWorkOrderId}
						</Text>
					</View>

					<Button
						mode="text"
						compact
						style={styles.viewButton}
						onPress={() => {
							onCancel();
							router.push(`/work-orders/${duplicateInfo.existingWorkOrderId}`);
						}}
					>
						View Existing Work Order
					</Button>

					<Text variant="bodyMedium" style={styles.message}>
						To create this as a duplicate, type the existing work order ID shown above into the field below:
					</Text>

					<TextInput
						label="Existing Work Order ID"
						value={ackInput}
						onChangeText={onAckInputChange}
						autoCapitalize="none"
						autoCorrect={false}
						style={styles.input}
						testID="duplicate-ack-input"
					/>

					<View style={styles.actions}>
						<Button mode="text" onPress={onCancel} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							mode="contained"
							onPress={onConfirm}
							disabled={!idMatches || isSubmitting}
							loading={isSubmitting}
						>
							Create Duplicate
						</Button>
					</View>
				</View>
			</Pressable>
		</Modal>
	);
};

const styles = StyleSheet.create({
	backdrop: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	container: {
		borderRadius: 12,
		padding: 24,
		maxWidth: 500,
		minWidth: 300,
		elevation: 8,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.25,
		shadowRadius: 8,
	},
	title: {
		marginBottom: 12,
		fontWeight: '600',
	},
	message: {
		marginBottom: 12,
		lineHeight: 22,
	},
	existingInfo: {
		padding: 12,
		borderRadius: 8,
		marginBottom: 8,
	},
	viewButton: {
		alignSelf: 'flex-start',
		marginBottom: 12,
	},
	input: {
		marginBottom: 16,
	},
	actions: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		gap: 8,
	},
});
